import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, Link } from 'react-router-dom';
import { Spinner, Text } from '@fluentui/react-components';
import { ChevronLeftRegular } from '@fluentui/react-icons';
import { BookingForm } from '@layouts/BookingForm';
import { HeroBanner } from '@components/common';
import { StepIndicator } from '@components/common/StepIndicator';
import { useBookingsScreenStyles } from './BookingsScreen.styles';
import { useBookingForm } from '@hooks/useBookingForm';
import { useDraftResume } from '@hooks/useDraftResume';
import { scrollAppToTop } from '@utils/scrollToTop';
import heroBannerImage from '@/assets/images/hero-banner.png';
import heroBannerImageDark from '@/assets/images/hero-banner-dark.png';
import { useTheme } from '@/theme';
import type { EventId } from '@store/models/ids';
import { ROUTES } from '@routes/routeConstants';

export const BookingFormScreen = () => {
  useEffect(() => {
    scrollAppToTop();
  }, []);
  const styles = useBookingsScreenStyles();
  const { t } = useTranslation('bookingForm');
  const { isDarkMode } = useTheme();
  const heroImage = isDarkMode ? heroBannerImageDark : heroBannerImage;
  const [searchParams] = useSearchParams();
  const eventIdParam = searchParams.get('eventId');
  const isResuming = !!eventIdParam;

  const {
    isLoading: resumeLoading,
    error: resumeError,
    building: resumeBuilding,
    buildingLoading: resumeBuildingLoading,
    spacesLoading: resumeSpacesLoading,
  } = useDraftResume(eventIdParam ? (eventIdParam as EventId) : null);

  const {
    formData,
    currentStep,
    highestVisitedStep,
    selectedSpace,
    availableSpaces,
    spacesLoading,
    spacesLoadingMore,
    spacesHasMore,
    spacesBatchError,
    loadMoreSpaces,
    submissionStatus,
    apiError,
    requestId,
    validationErrors,
    dynamicConfig,
    selectedBuilding,
    handleFieldChange,
    handleBack,
    handleStepClick,
    handleSelectSpace,
    handleSetupChange,
    handleBookAnother,
    handleGoHome,
    onNext,
    onSaveAsDraft,
    onDiscard,
    onSubmit,
    canSubmit,
    canSaveDraft,
    onRetrySpaces,
    onAmenityFilterChange,
    stepCompletionMap,
    backDisabledOnReview,
    handleInlineFieldChange,
    handleUpdateFilters,
    filterFields,
  } = useBookingForm({ isResuming });

  // `resumeBuilding` is a fast-path fallback captured once at draft load, so it
  // must only be trusted while it still matches the building currently selected
  // in the form — otherwise changing the building on a resumed draft would keep
  // showing the originally-saved building on Review/Confirmation.
  const currentBuildingId = formData.buildingId as string | undefined;
  const effectiveBuilding =
    selectedBuilding?.buildingId === currentBuildingId
      ? selectedBuilding
      : resumeBuilding?.buildingId === currentBuildingId
        ? resumeBuilding
        : (selectedBuilding ?? resumeBuilding);

  if (resumeLoading) {
    return (
      <div className={styles.root}>
        <div className={styles.scrollArea}>
          <div className={styles.loadingContent} data-testid="draft-resume-loading">
            <Spinner
              label={String(t('bookingForm.messages.loading', { defaultValue: 'Loading...' }))}
            />
          </div>
        </div>
      </div>
    );
  }

  if (resumeError) {
    return (
      <div className={styles.root}>
        <div className={styles.scrollArea}>
          <div className={styles.formContent}>
            <Text>{resumeError}</Text>
            <Link to={ROUTES.BOOKINGS}>Back to Bookings</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.scrollArea}>
        {submissionStatus !== 'submitted' &&
          (() => {
            const activeStep = dynamicConfig.steps[currentStep];
            const bannerTitle = activeStep?.bannerTitleKey
              ? String(t(activeStep.bannerTitleKey))
              : String(t('bookingForm.hero.title'));
            const bannerSubtitle = activeStep?.bannerDescriptionKey
              ? String(t(activeStep.bannerDescriptionKey))
              : String(t('bookingForm.hero.subtitle'));
            return (
              <>
                <HeroBanner
                  figmaVariant
                  title={bannerTitle}
                  subtitle={bannerSubtitle}
                  backgroundImage={heroImage}
                  backLink={
                    <Link to={ROUTES.BOOKINGS} className={styles.heroBackLink}>
                      <ChevronLeftRegular fontSize={20} aria-hidden="true" />
                      <span>{String(t('bookingForm.hero.backToBookings'))}</span>
                    </Link>
                  }
                />
                <div className={styles.stickyStepperWrapper} data-testid="sticky-stepper-wrapper">
                  <StepIndicator
                    figmaVariant
                    steps={dynamicConfig.steps}
                    currentStep={currentStep}
                    highestVisitedStep={highestVisitedStep}
                    onStepClick={(stepIndex) => void handleStepClick(stepIndex)}
                    clickableMode="visited-and-current"
                    compactBreakpoint="never"
                  />
                </div>
              </>
            );
          })()}
        <div className={styles.formContent}>
          <BookingForm
            config={dynamicConfig}
            formData={formData}
            currentStep={currentStep}
            highestVisitedStep={highestVisitedStep}
            selectedSpace={selectedSpace}
            availableSpaces={availableSpaces}
            spacesLoading={spacesLoading}
            validationErrors={validationErrors}
            submissionStatus={submissionStatus}
            apiError={apiError}
            requestId={requestId}
            selectedBuilding={effectiveBuilding}
            buildingLoading={resumeBuildingLoading}
            resumeSpacesLoading={resumeSpacesLoading}
            stepCompletionMap={stepCompletionMap}
            onFieldChange={handleFieldChange}
            onNext={onNext}
            onBack={handleBack}
            onSaveAsDraft={onSaveAsDraft}
            onDiscard={onDiscard}
            onSubmit={onSubmit}
            canSubmit={canSubmit}
            canSaveDraft={canSaveDraft}
            onStepClick={(stepIndex) => void handleStepClick(stepIndex)}
            onSelectSpace={handleSelectSpace}
            onSetupChange={handleSetupChange}
            onBookAnother={handleBookAnother}
            onGoHome={handleGoHome}
            onRetrySpaces={onRetrySpaces}
            onAmenityFilterChange={onAmenityFilterChange}
            onLoadMore={() => void loadMoreSpaces()}
            onRetryBatch={() => void loadMoreSpaces()}
            hasMore={spacesHasMore}
            isLoadingMore={spacesLoadingMore}
            batchError={spacesBatchError}
            backDisabledOnReview={backDisabledOnReview}
            filterFields={filterFields}
            onInlineFieldChange={handleInlineFieldChange}
            onUpdateFilters={handleUpdateFilters}
            filterValidationErrors={validationErrors}
          />
        </div>
      </div>
    </div>
  );
};

BookingFormScreen.displayName = 'BookingFormScreen';
